import React from "react";
import {
    Star,
    Quote,
    AlertTriangle,
    CheckCircle2,
    ArrowRight,
    Bell,
    Workflow,
    Smartphone,
    Zap,
    Shield,
    Users,
    Clock,
} from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../../components/ui/accordion";
import { CTA, traders } from "./SectionsTop";

function CommunitySection() {
    const tiles = [...traders, ...traders];
    return (
        <section
            data-testid="section-community"
            className="bg-white text-[var(--mbe-text-light)] py-24 lg:py-32"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto">
                    <span className="tag-pill bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <Users className="w-3.5 h-3.5" />
                        Trusted Community
                    </span>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-5 leading-tight">
                        5,000+ traders. <span className="text-[var(--mbe-gold-2)]">One system.</span>
                    </h2>
                    <p className="mt-4 text-slate-600">
                        From SEA to Africa to the Middle East, MBE traders rely on
                        MBE Alert to stay ahead without staring at the screen.
                    </p>
                </div>
                <div className="mt-12 overflow-hidden">
                    <div className="marquee-track flex gap-5 w-max">
                        {tiles.map((src, i) => (
                            <div
                                key={i}
                                className="w-56 h-72 rounded-2xl overflow-hidden border border-slate-200 shadow-sm flex-shrink-0 relative group"
                            >
                                <img
                                    src={src}
                                    alt="MBE Alert community trader using real time Telegram alerts"
                                    loading="lazy"
                                    decoding="async"
                                    className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0d14]/80 via-transparent" />
                                <div className="absolute bottom-3 left-3 right-3 text-white">
                                    <p className="text-xs uppercase tracking-[0.18em] text-amber-300">
                                        Verified member
                                    </p>
                                    <p className="text-sm font-medium">
                                        Trader · MBE Alert
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
                    {[
                        ["5,000+", "Active members"],
                        ["3", "Continents"],
                        ["24/7", "Auto delivery"],
                        ["98%", "Stay past trial"],
                    ].map(([v, l], i) => (
                        <div
                            key={i}
                            className="text-center py-5 rounded-2xl border border-slate-200 bg-slate-50/60"
                        >
                            <p className="font-display text-2xl sm:text-3xl font-extrabold text-[var(--mbe-text-light)]">
                                {v}
                            </p>
                            <p className="text-xs uppercase tracking-[0.15em] mt-1 text-slate-500">
                                {l}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function TestimonialsSection() {
    const t = [
        {
            name: "Aiman R.",
            role: "Swing Trader · Malaysia",
            text: "I used to miss zones because I was at work. MBE Alert just pings me. Clean, simple. I'm not glued to my screen anymore.",
        },
        {
            name: "Kwame O.",
            role: "Forex Trader · Nigeria",
            text: "The structured format saves me from panic entries. I see pair, TF, entry, and I decide. No emotion, no chasing.",
        },
        {
            name: "Tariq H.",
            role: "Part-time Trader · UAE",
            text: "Honestly the workflow upgrade I didn't know I needed. Less screen time, more confidence in the setups I take.",
        },
        {
            name: "Lina S.",
            role: "Day Trader · Indonesia",
            text: "Used to scroll charts for hours. Now I wait for the alert. My missed setups dropped to almost zero.",
        },
        {
            name: "Daniel P.",
            role: "Remote Trader · South Africa",
            text: "It feels less like a bot and more like a teammate that taps your shoulder when it matters.",
        },
        {
            name: "Reza M.",
            role: "Trader · Iran",
            text: "Clean, fast, no noise. Exactly what a real MBE workflow should feel like.",
        },
    ];
    return (
        <section
            data-testid="section-testimonials"
            className="bg-[var(--mbe-bg-light-2)] text-[var(--mbe-text-light)] py-24 lg:py-32"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto">
                    <span className="tag-pill bg-white text-slate-600 border border-slate-200">
                        Real Trader Feedback
                    </span>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-5 leading-tight">
                        Less screen time.
                        <span className="text-[var(--mbe-gold-2)]"> More clarity.</span>
                    </h2>
                </div>
                <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {t.map((it, i) => (
                        <div
                            key={i}
                            data-testid={`testimonial-${i}`}
                            className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-[0_24px_60px_-30px_rgba(15,23,42,0.3)] transition"
                        >
                            <div className="flex items-center gap-1 text-[var(--mbe-gold)]">
                                {Array.from({ length: 5 }).map((_, k) => (
                                    <Star key={k} className="w-4 h-4 fill-current" />
                                ))}
                            </div>
                            <Quote className="w-6 h-6 text-slate-300 mt-4" />
                            <p className="mt-3 text-slate-700 leading-relaxed">
                                &ldquo;{it.text}&rdquo;
                            </p>
                            <div className="mt-6 pt-5 border-t border-slate-100">
                                <p className="font-display font-semibold">{it.name}</p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {it.role}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function ContrastPainSection() {
    const oldWay = [
        "Staring at charts for 10+ hours",
        "Missing zones while at work",
        "Reacting late, chasing the candle",
        "Emotional, rushed entries",
    ];
    const newWay = [
        "Live in your day, alert pings you",
        "Catch zones the moment they form",
        "Clean structured info, then decide",
        "Calm, structured, on-time entries",
    ];
    return (
        <section
            data-testid="section-contrast"
            className="bg-[var(--mbe-bg-dark)] text-white py-24 lg:py-32 relative overflow-hidden"
        >
            <div className="absolute inset-0 bg-grid-dark opacity-25" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto">
                    <span className="tag-pill bg-white/5 text-slate-300 border border-white/10">
                        Reality Check
                    </span>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-5 leading-tight">
                        You don&apos;t need more chart hours.
                        <br />
                        <span className="text-[var(--mbe-gold)]">
                            You need better timing.
                        </span>
                    </h2>
                </div>
                <div className="mt-14 grid md:grid-cols-2 gap-5">
                    <div className="rounded-3xl p-8 border border-rose-500/20 bg-gradient-to-br from-rose-500/10 to-transparent">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-rose-500/15 grid place-items-center">
                                <AlertTriangle className="w-5 h-5 text-rose-400" />
                            </div>
                            <p className="font-display text-xl font-semibold">
                                The Old Way
                            </p>
                        </div>
                        <ul className="mt-6 space-y-3">
                            {oldWay.map((p, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-3 text-slate-300"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-2 flex-shrink-0" />
                                    {p}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="rounded-3xl p-8 border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-transparent">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 grid place-items-center">
                                <Bell className="w-5 h-5 text-emerald-400" />
                            </div>
                            <p className="font-display text-xl font-semibold">
                                The MBE Alert Way
                            </p>
                        </div>
                        <ul className="mt-6 space-y-3">
                            {newWay.map((p, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-3 text-slate-200"
                                >
                                    <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-1 flex-shrink-0" />
                                    {p}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}

function WorkflowTimelineSection() {
    const steps = [
        {
            title: "Stop relying on manual checking",
            desc: "Drop the scroll. Drop the screen anxiety. Let the system watch the chart for you.",
            icon: AlertTriangle,
        },
        {
            title: "Use a real-time alert system",
            desc: "MBE Alert pings you the second a valid bubble zone forms, with pair, timeframe, and entry included.",
            icon: Bell,
        },
        {
            title: "Review setup with more clarity",
            desc: "Clean structured format. Skim, confirm, no second-guessing.",
            icon: Workflow,
        },
        {
            title: "Execute with more structure",
            desc: "No emotional chase. Just calm, on-time, structured entries.",
            icon: CheckCircle2,
        },
    ];
    return (
        <section
            data-testid="section-workflow"
            className="bg-[var(--mbe-bg-dark)] text-white py-24 lg:py-32 relative overflow-hidden border-t border-white/5"
        >
            <div className="absolute inset-0 opacity-[0.06] bg-cover bg-center" style={{ backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/c97944d1-40eb-4223-b474-bd3b37680737/images/b108e554fd416f2961745614dd7a5772932d32de5249780a0d42e4f3bee8c859.png)' }} />
            <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto">
                    <span className="tag-pill bg-[var(--mbe-gold)]/15 text-[var(--mbe-gold)] border border-[var(--mbe-gold)]/30">
                        Workflow Upgrade
                    </span>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-5 leading-tight">
                        How to actually improve your timing.
                    </h2>
                </div>
                <div className="mt-16 relative">
                    <div className="absolute left-6 top-2 bottom-2 w-px bg-gradient-to-b from-[var(--mbe-gold)]/60 via-white/15 to-transparent" />
                    <div className="space-y-6">
                        {steps.map((s, i) => (
                            <div
                                key={i}
                                className="relative pl-20"
                                data-testid={`workflow-step-${i}`}
                            >
                                <div className="absolute left-0 top-0 w-12 h-12 rounded-2xl bg-[var(--mbe-gold)] grid place-items-center shadow-[0_0_30px_rgba(229,169,61,0.4)]">
                                    <s.icon className="w-5 h-5 text-[#0a0d14]" strokeWidth={2.5} />
                                </div>
                                <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur p-6">
                                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
                                        Step {String(i + 1).padStart(2, "0")}
                                    </p>
                                    <p className="mt-2 font-display text-xl font-semibold">
                                        {s.title}
                                    </p>
                                    <p className="mt-2 text-slate-400 leading-relaxed">
                                        {s.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function OfferSection() {
    return (
        <section
            data-testid="section-offer"
            className="bg-[var(--mbe-bg-dark)] text-white py-24 lg:py-32 relative overflow-hidden"
        >
            <div className="absolute inset-0 bg-grid-dark opacity-30" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[var(--mbe-gold)]/15 rounded-full blur-[160px]" />
            <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <span className="tag-pill bg-[var(--mbe-gold)]/15 text-[var(--mbe-gold)] border border-[var(--mbe-gold)]/40">
                    <Clock className="w-3.5 h-3.5" />
                    Limited Trial Batch · Closing Soon
                </span>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-6 leading-[1.05]">
                    5 days inside MBE Alert. <br />
                    <span className="text-[var(--mbe-gold)] gold-text-glow">
                        Yours to test in real time.
                    </span>
                </h2>
                <p className="mt-6 text-slate-300 max-w-2xl mx-auto text-lg">
                    Plug it into your Telegram. Use it through real market sessions.
                    Feel the difference between watching charts and being told the
                    moment a valid zone forms.
                </p>

                <div className="mt-10 grid sm:grid-cols-3 gap-3 max-w-2xl mx-auto">
                    {[
                        ["5 days", "Full access"],
                        ["1 webinar", "Live trade walkthrough"],
                        ["0 risk", "Cancel anytime"],
                    ].map(([v, l], i) => (
                        <div
                            key={i}
                            className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur p-5"
                        >
                            <p className="font-display text-2xl font-bold text-[var(--mbe-gold)]">
                                {v}
                            </p>
                            <p className="text-xs uppercase tracking-[0.15em] text-slate-400 mt-1">
                                {l}
                            </p>
                        </div>
                    ))}
                </div>

                <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center items-center">
                    <CTA
                        location="offer-primary"
                        section="offer"
                        label="Activate MBE Alert"
                    />
                    <a
                        href="#faq"
                        data-testid="offer-secondary-link"
                        className="btn-ghost-dark"
                    >
                        Read FAQ
                    </a>
                </div>
                <p className="mt-5 text-xs uppercase tracking-[0.18em] text-slate-500">
                    Slots refresh in batches · join while access is open
                </p>
            </div>
        </section>
    );
}

function StepsSection() {
    const steps = [
        {
            n: "01",
            title: "Claim your trial",
            desc: "Tap Start 5 Day Trial. We'll open Telegram and onboard you in seconds.",
            icon: Zap,
        },
        {
            n: "02",
            title: "Connect to MBE Alert",
            desc: "You get added to the live alert channel. Zero install, zero setup mess.",
            icon: Smartphone,
        },
        {
            n: "03",
            title: "Trade on time, not late",
            desc: "Receive structured alerts during the next 5 days, plus the live webinar.",
            icon: Shield,
        },
    ];
    return (
        <section
            data-testid="section-steps"
            className="bg-white text-[var(--mbe-text-light)] py-24 lg:py-32"
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto">
                    <span className="tag-pill bg-slate-100 text-slate-600">
                        Get started in 3 steps
                    </span>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-5 leading-tight">
                        Connect in under <span className="text-[var(--mbe-gold-2)]">2 minutes.</span>
                    </h2>
                </div>
                <div className="mt-14 grid md:grid-cols-3 gap-5">
                    {steps.map((s, i) => (
                        <div
                            key={i}
                            data-testid={`step-${i}`}
                            className="relative bg-slate-50/60 border border-slate-200 rounded-2xl p-7 hover:border-[var(--mbe-gold)]/40 hover:bg-white transition"
                        >
                            <span className="absolute top-5 right-6 font-display text-7xl font-extrabold text-slate-200/80 select-none">
                                {s.n}
                            </span>
                            <div className="relative w-11 h-11 rounded-xl bg-[var(--mbe-gold)]/15 grid place-items-center">
                                <s.icon className="w-5 h-5 text-[var(--mbe-gold-2)]" />
                            </div>
                            <h3 className="relative mt-5 font-display font-semibold text-xl">
                                {s.title}
                            </h3>
                            <p className="relative mt-2 text-sm text-slate-600 leading-relaxed">
                                {s.desc}
                            </p>
                        </div>
                    ))}
                </div>
                <div className="mt-12 text-center">
                    <CTA
                        location="steps-primary"
                        section="steps"
                        label="Start 5-Day Trial"
                    />
                </div>
            </div>
        </section>
    );
}

function WrapUpSection() {
    return (
        <section
            data-testid="section-wrap-up"
            className="bg-[var(--mbe-bg-dark)] text-white py-28 lg:py-40 relative overflow-hidden"
        >
            <div className="absolute inset-0 bg-grid-dark opacity-25" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[var(--mbe-gold)]/10 rounded-full blur-[140px]" />
            <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <Quote className="w-10 h-10 text-[var(--mbe-gold)] mx-auto" />
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-6 leading-tight">
                    &ldquo;The trader who reacts on time
                    <br />
                    <span className="text-[var(--mbe-gold)]">
                        beats the trader who watches longer.&rdquo;
                    </span>
                </h2>
                <p className="mt-8 text-slate-400">
                    MBE Alert isn&apos;t here to replace your judgement. It&apos;s here to make
                    sure you never miss the moment to use it.
                </p>
                <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                    <CTA
                        location="wrap-primary"
                        section="wrap-up"
                        label="Start 5-Day Trial"
                    />
                </div>
            </div>
        </section>
    );
}

function FAQSection() {
    const faqs = [
        {
            q: "What is MBE Alert?",
            a: "MBE Alert is a real time Telegram alert system that notifies you the moment price enters a valid MBE bubble zone, with pair, timeframe, and entry already structured.",
        },
        {
            q: "Do I need to watch charts all day?",
            a: "No. That's exactly the problem MBE Alert solves. You live your day, the system watches the chart, and pings you only when a valid zone forms.",
        },
        {
            q: "What information do I receive in each alert?",
            a: "Every alert includes the pair, the timeframe (e.g. M15, M30, H1), and the entry price, in a clean, repeatable format that takes 5 seconds to read.",
        },
        {
            q: "Is this a signal group?",
            a: "No. MBE Alert is not a copy-trade signal service. It's a structured alert layer for traders who already use the MBE system, designed to upgrade timing and workflow.",
        },
        {
            q: "Is this suitable for beginners?",
            a: "MBE Alert is most powerful for traders already familiar with the MBE system. Beginners can use it, but the live webinar will help bridge the gap quickly.",
        },
        {
            q: "What do I get in the 5-day trial?",
            a: "Full access to live MBE Alert delivery on Telegram for 5 days, plus the Free Live-Trade Webinar showing exactly how to use the alerts.",
        },
        {
            q: "Is the webinar included?",
            a: "Yes. The Free Live-Trade Webinar Using MBE Alert is included for every active trial member during the trial window.",
        },
        {
            q: "Do I need to install anything complicated?",
            a: "No. If you have Telegram, you're ready. We onboard you into the alert channel directly. Zero setup mess.",
        },
        {
            q: "Does MBE Alert guarantee profit?",
            a: "No tool can guarantee profit. MBE Alert is built to improve timing, structure, and workflow. Your judgement and risk management still matter.",
        },
        {
            q: "Who is MBE Alert best for?",
            a: "Busy traders, part-time traders, and MBE users who keep missing clean setups because they can't sit at the screen all day.",
        },
    ];
    return (
        <section
            data-testid="section-faq"
            id="faq"
            className="bg-white text-[var(--mbe-text-light)] py-24 lg:py-32"
        >
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center">
                    <span className="tag-pill bg-slate-100 text-slate-600">FAQ</span>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-5 leading-tight">
                        Honest answers,
                        <span className="text-[var(--mbe-gold-2)]"> no fluff.</span>
                    </h2>
                </div>
                <Accordion type="single" collapsible className="mt-12">
                    {faqs.map((f, i) => (
                        <AccordionItem
                            key={i}
                            value={`item-${i}`}
                            data-testid={`faq-item-${i}`}
                            className="border-b border-slate-200"
                        >
                            <AccordionTrigger className="text-left font-display text-base sm:text-lg font-semibold py-5 hover:text-[var(--mbe-gold-2)]">
                                {f.q}
                            </AccordionTrigger>
                            <AccordionContent className="text-slate-600 leading-relaxed pb-6">
                                {f.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
                <div className="mt-12 text-center">
                    <CTA
                        location="faq-primary"
                        section="faq"
                        label="Start 5-Day Trial"
                    />
                </div>
            </div>
        </section>
    );
}

function Footer() {
    return (
        <footer
            data-testid="footer"
            className="bg-[var(--mbe-bg-dark)] text-slate-400 border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
                <div className="grid md:grid-cols-3 gap-10">
                    <div>
                        <div className="flex items-center gap-2 text-white">
                            <div className="w-9 h-9 rounded-xl bg-[var(--mbe-gold)] grid place-items-center text-[#0a0d14]">
                                <Bell className="w-5 h-5" strokeWidth={2.5} />
                            </div>
                            <span className="font-display text-lg font-bold">
                                MBE <span className="text-[var(--mbe-gold)]">Alert</span>
                            </span>
                        </div>
                        <p className="mt-4 text-sm leading-relaxed max-w-xs">
                            Real-time Telegram alerts for valid MBE bubble zones.
                            Built for traders who want better timing, not more
                            screen time.
                        </p>
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                            Navigate
                        </p>
                        <ul className="mt-4 space-y-2 text-sm">
                            <li><a href="#features" className="hover:text-white transition">Features</a></li>
                            <li><a href="#bonus" className="hover:text-white transition">Bonus Webinar</a></li>
                            <li><a href="#faq" className="hover:text-white transition">FAQ</a></li>
                        </ul>
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                            Risk Disclaimer
                        </p>
                        <p className="mt-4 text-xs leading-relaxed">
                            Trading involves risk. MBE Alert is designed as a support
                            tool to improve workflow and awareness, not to guarantee
                            results. You are responsible for your own decisions.
                        </p>
                    </div>
                </div>
                <div className="mt-10 pt-6 border-t border-white/5 flex flex-col sm:flex-row gap-3 justify-between items-start text-xs text-slate-500">
                    <p>© {new Date().getFullYear()} MBE Alert. All rights reserved.</p>
                    <p>Built for traders, not gamblers.</p>
                </div>
            </div>
        </footer>
    );
}

export {
    CommunitySection,
    TestimonialsSection,
    ContrastPainSection,
    WorkflowTimelineSection,
    OfferSection,
    StepsSection,
    WrapUpSection,
    FAQSection,
    Footer,
};
